import DevicePushControl from './DevicePushControl';
import React, {
  useState,
  useEffect,
  useRef
} from 'react';
import {
  get,
  post,
  put,
  del
} from '../utils/api';

const TYPE_COLORS = {
  info: {
    bg:'#EDF4FF',
    color:'#1E4FAD',
    icon:'ℹ'
  },
  success: {
    bg:'#E8F5EB',
    color:'#2E6B3A',
    icon:'✓'
  },
  warning: {
    bg:'#FFF3DC',
    color:'#8B5E0A',
    icon:'⚠'
  },
  danger: {
    bg:'#FDEAEA',
    color:'#8F2E24',
    icon:'🔴'
  }
};

const DEFAULT_VOLUME = 0.65;

export default function NotificationBell() {
  const [
    notifications,
    setNotifications
  ] = useState([]);

  const [unread, setUnread] =
    useState(0);

  const [open, setOpen] =
    useState(false);

  const [
    soundEnabled,
    setSoundEnabled
  ] = useState(false);

  const [
    soundVolume,
    setSoundVolume
  ] = useState(DEFAULT_VOLUME);

  const [
    soundSaving,
    setSoundSaving
  ] = useState(false);

  const ref = useRef(null);
  const audioContextRef = useRef(null);
  const audioReadyRef = useRef(false);
  const soundEnabledRef = useRef(false);
  const soundVolumeRef =
    useRef(DEFAULT_VOLUME);
  const claimingRef = useRef(false);

  const navigate = path => {
    window.location.href = path;
  };

  const load = () => {
    get('/notifications')
      .then(data => {
        setNotifications(
          data.data || []
        );
        setUnread(
          data.unread_count || 0
        );
      })
      .catch(() => {});
  };

  const unlockAudio = async () => {
    const AudioContext =
      window.AudioContext ||
      window.webkitAudioContext;

    if (!AudioContext) {
      throw new Error(
        'Audio is not supported by this browser'
      );
    }

    if (!audioContextRef.current) {
      audioContextRef.current =
        new AudioContext();
    }

    if (
      audioContextRef.current.state ===
      'suspended'
    ) {
      await audioContextRef.current
        .resume();
    }

    audioReadyRef.current =
      audioContextRef.current.state ===
      'running';

    return audioReadyRef.current;
  };

  const playChime = async (
    volume,
    critical=false
  ) => {
    const ready = await unlockAudio();

    if (!ready) {
      throw new Error(
        'Browser audio is blocked'
      );
    }

    const context =
      audioContextRef.current;

    const gain =
      context.createGain();

    gain.connect(
      context.destination
    );

    const now =
      context.currentTime;

    const safeVolume =
      Math.max(
        0,
        Math.min(
          1,
          Number(volume) ||
            DEFAULT_VOLUME
        )
      );

    gain.gain.setValueAtTime(
      0.0001,
      now
    );

    gain.gain.exponentialRampToValueAtTime(
      Math.max(
        0.01,
        safeVolume * 0.16
      ),
      now + 0.02
    );

    gain.gain.exponentialRampToValueAtTime(
      0.0001,
      now + (
        critical ? 0.75 : 0.55
      )
    );

    const frequencies =
      critical
        ? [660, 880, 1046]
        : [660, 880];

    frequencies.forEach(
      (frequency, index) => {
        const oscillator =
          context.createOscillator();

        oscillator.type = 'sine';
        oscillator.frequency.value =
          frequency;

        oscillator.connect(gain);

        const start =
          now + index * 0.15;

        oscillator.start(start);
        oscillator.stop(
          start + 0.22
        );
      }
    );
  };

  const claimAndPlaySound =
    async () => {
      if (
        claimingRef.current ||
        !soundEnabledRef.current ||
        !audioReadyRef.current
      ) {
        return;
      }

      claimingRef.current = true;

      try {
        const result =
          await post(
            '/notifications/sound/claim',
            {}
          );

        if (result.data) {
          await playChime(
            soundVolumeRef.current,
            result.data.priority ===
              'critical'
          );
        }
      } catch (error) {
        console.warn(
          'Notification sound unavailable:',
          error.message
        );
      } finally {
        claimingRef.current = false;
      }
    };

  useEffect(() => {
    let active = true;

    Promise.all([
      get('/notifications'),
      get('/notifications/preferences')
    ])
      .then(
        ([
          notificationData,
          preferenceData
        ]) => {
          if (!active) {
            return;
          }

          setNotifications(
            notificationData.data || []
          );

          setUnread(
            notificationData
              .unread_count || 0
          );

          const preference =
            preferenceData.data || {};

          const enabled =
            preference.sound_enabled ===
            true;

          const volume =
            Number(
              preference.sound_volume
            );

          soundEnabledRef.current =
            enabled;

          soundVolumeRef.current =
            Number.isFinite(volume)
              ? volume
              : DEFAULT_VOLUME;

          setSoundEnabled(enabled);
          setSoundVolume(
            soundVolumeRef.current
          );
        }
      )
      .catch(() => {});

    const unlock = () => {
      unlockAudio()
        .then(() => {
          claimAndPlaySound();
        })
        .catch(() => {});
    };

    document.addEventListener(
      'pointerdown',
      unlock,
      { once:true }
    );

    document.addEventListener(
      'keydown',
      unlock,
      { once:true }
    );

    const interval =
      window.setInterval(() => {
        load();
        claimAndPlaySound();
      }, 30000);

    return () => {
      active = false;

      window.clearInterval(interval);

      document.removeEventListener(
        'pointerdown',
        unlock
      );

      document.removeEventListener(
        'keydown',
        unlock
      );

      if (audioContextRef.current) {
        audioContextRef.current
          .close()
          .catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    const handler = event => {
      if (
        ref.current &&
        !ref.current.contains(
          event.target
        )
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handler
    );

    return () =>
      document.removeEventListener(
        'mousedown',
        handler
      );
  }, []);

  const toggleSound = async () => {
    if (soundSaving) {
      return;
    }

    const nextEnabled =
      !soundEnabled;

    setSoundSaving(true);

    try {
      if (nextEnabled) {
        await playChime(
          soundVolume,
          false
        );
      }

      const result = await put(
        '/notifications/preferences',
        {
          sound_enabled:
            nextEnabled,
          sound_volume:
            soundVolume
        }
      );

      const saved =
        result.data || {};

      soundEnabledRef.current =
        saved.sound_enabled === true;

      soundVolumeRef.current =
        Number(
          saved.sound_volume
        ) || DEFAULT_VOLUME;

      setSoundEnabled(
        soundEnabledRef.current
      );

      setSoundVolume(
        soundVolumeRef.current
      );
    } catch (error) {
      window.alert(
        error.message ||
        'Notification sound could not be updated.'
      );
    } finally {
      setSoundSaving(false);
    }
  };

  const handleMarkAllRead =
    async () => {
      await put(
        '/notifications/read-all',
        {}
      );
      load();
    };

  const handleClear = async () => {
    await del(
      '/notifications/clear'
    );
    load();
  };

  const handleClick = async (
    notification
  ) => {
    await put(
      '/notifications/' +
        notification.id +
        '/read',
      {}
    );

    setOpen(false);

    if (notification.link) {
      navigate(notification.link);
    }

    load();
  };

  return (
    <div
      ref={ref}
      style={{
        position:'relative'
      }}
    >
      <button
        onClick={() =>
          setOpen(!open)
        }
        style={{
          position:'relative',
          background:'none',
          border:'none',
          cursor:'pointer',
          padding:'6px 8px',
          borderRadius:8,
          display:'flex',
          alignItems:'center',
          color:
            'rgba(242,239,231,.7)',
          fontSize:20,
          transition:'color .15s'
        }}
        title="Notifications"
      >
        🔔

        {unread > 0 && (
          <span
            style={{
              position:'absolute',
              top:2,
              right:2,
              background:'#8F2E24',
              color:'#fff',
              borderRadius:'50%',
              width:16,
              height:16,
              fontSize:9,
              fontWeight:700,
              display:'flex',
              alignItems:'center',
              justifyContent:'center',
              lineHeight:1
            }}
          >
            {unread > 9
              ? '9+'
              : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position:'absolute',
            right:0,
            top:'calc(100% + 8px)',
            width:360,
            background:'#fff',
            borderRadius:10,
            boxShadow:
              '0 8px 32px rgba(0,0,0,.15)',
            border:
              '1px solid var(--border-light)',
            zIndex:1000,
            overflow:'hidden'
          }}
        >
          <div
            style={{
              background:'#3B2A21',
              padding:'12px 16px',
              display:'flex',
              justifyContent:
                'space-between',
              alignItems:'center'
            }}
          >
            <span
              style={{
                fontFamily:
                  'Cormorant Garamond,serif',
                fontSize:'1rem',
                color:'#A8893D',
                fontWeight:600
              }}
            >
              Notifications
              {unread > 0 &&
                ` (${unread})`}
            </span>

            <div
              style={{
                display:'flex',
                gap:8
              }}
            >
              {unread > 0 && (
                <button
                  onClick={
                    handleMarkAllRead
                  }
                  style={{
                    background:'none',
                    border:'none',
                    color:
                      'rgba(242,239,231,.5)',
                    fontSize:10,
                    cursor:'pointer',
                    padding:0
                  }}
                >
                  Mark all read
                </button>
              )}

              <button
                onClick={handleClear}
                style={{
                  background:'none',
                  border:'none',
                  color:
                    'rgba(242,239,231,.5)',
                  fontSize:10,
                  cursor:'pointer',
                  padding:0
                }}
              >
                Clear read
              </button>
            </div>
          </div>

          <div
            style={{
              padding:'10px 16px',
              background:'#F8F5EE',
              borderBottom:
                '1px solid #E7E0D5',
              display:'flex',
              alignItems:'center',
              justifyContent:
                'space-between',
              gap:12
            }}
          >
            <div>
              <div
                style={{
                  fontSize:12,
                  fontWeight:600,
                  color:'#3B2A21'
                }}
              >
                Priority notification sound
              </div>

              <div
                style={{
                  fontSize:10,
                  color:'#8A8A8A',
                  marginTop:2
                }}
              >
                Important and critical alerts only
              </div>
            </div>

            <button
              type="button"
              onClick={toggleSound}
              disabled={soundSaving}
              style={{
                border:
                  soundEnabled
                    ? '1px solid #556B2F'
                    : '1px solid #B9B1A5',
                background:
                  soundEnabled
                    ? '#556B2F'
                    : '#fff',
                color:
                  soundEnabled
                    ? '#fff'
                    : '#3B2A21',
                borderRadius:14,
                padding:'5px 10px',
                fontSize:10,
                fontWeight:700,
                cursor:
                  soundSaving
                    ? 'wait'
                    : 'pointer'
              }}
            >
              {soundSaving
                ? 'Saving...'
                : soundEnabled
                  ? 'Sound on'
                  : 'Enable sound'}
            </button>
          </div>

          <DevicePushControl />

          <div
            style={{
              maxHeight:400,
              overflowY:'auto'
            }}
          >
            {notifications.length === 0
              ? (
                <div
                  style={{
                    padding:24,
                    textAlign:'center',
                    color:'#8A8A8A',
                    fontSize:13
                  }}
                >
                  No notifications yet
                </div>
              )
              : notifications.map(
                notification => {
                  const colors =
                    TYPE_COLORS[
                      notification.type
                    ] ||
                    TYPE_COLORS.info;

                  return (
                    <div
                      key={
                        notification.id
                      }
                      onClick={() =>
                        handleClick(
                          notification
                        )
                      }
                      style={{
                        padding:
                          '12px 16px',
                        borderBottom:
                          '1px solid #F2EFE7',
                        cursor:
                          notification.link
                            ? 'pointer'
                            : 'default',
                        background:
                          notification.is_read
                            ? '#fff'
                            : '#FFFBF0',
                        transition:
                          'background .1s'
                      }}
                    >
                      <div
                        style={{
                          display:'flex',
                          gap:10,
                          alignItems:
                            'flex-start'
                        }}
                      >
                        <span
                          style={{
                            flexShrink:0,
                            width:28,
                            height:28,
                            borderRadius:
                              '50%',
                            background:
                              colors.bg,
                            color:
                              colors.color,
                            display:'flex',
                            alignItems:
                              'center',
                            justifyContent:
                              'center',
                            fontSize:13,
                            fontWeight:700
                          }}
                        >
                          {colors.icon}
                        </span>

                        <div
                          style={{
                            flex:1,
                            minWidth:0
                          }}
                        >
                          <div
                            style={{
                              fontSize:13,
                              fontWeight:
                                notification
                                  .is_read
                                  ? 400
                                  : 600,
                              color:'#3B2A21',
                              marginBottom:2,
                              overflow:'hidden',
                              textOverflow:
                                'ellipsis',
                              whiteSpace:
                                'nowrap'
                            }}
                          >
                            {
                              notification
                                .title
                            }
                          </div>

                          {notification
                            .message && (
                            <div
                              style={{
                                fontSize:11,
                                color:
                                  '#8A8A8A',
                                lineHeight:1.4,
                                overflow:
                                  'hidden',
                                textOverflow:
                                  'ellipsis',
                                display:
                                  '-webkit-box',
                                WebkitLineClamp:
                                  2,
                                WebkitBoxOrient:
                                  'vertical'
                              }}
                            >
                              {
                                notification
                                  .message
                              }
                            </div>
                          )}

                          <div
                            style={{
                              fontSize:10,
                              color:'#8A8A8A',
                              marginTop:4
                            }}
                          >
                            {new Date(
                              notification
                                .created_at
                            ).toLocaleString(
                              'en-NG'
                            )}
                          </div>
                        </div>

                        {!notification
                          .is_read && (
                          <div
                            style={{
                              width:8,
                              height:8,
                              borderRadius:
                                '50%',
                              background:
                                '#8F2E24',
                              flexShrink:0,
                              marginTop:4
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                }
              )}
          </div>
        </div>
      )}
    </div>
  );
}
